import type { LocaleIso } from './locale';

/**
 * Formats a date string according to the specified locale and options.
 *
 * @param dateString - The ISO date string to format (e.g., '2021-02-03T14:42:14.177Z').
 * @param locale - The locale to use for formatting (e.g., 'de-DE', 'en-GB'). Defaults to 'de-DE'.
 * @param options - Optional Intl.DateTimeFormatOptions to customize the output.
 *   By default, uses { year: 'numeric', month: '2-digit', day: '2-digit' }.
 * @returns The formatted date string according to the locale and options, or an empty string if input is invalid.
 *
 * @example
 * formatDateByLocale('2021-02-03T14:42:14.177Z', 'en-GB'); // '03/02/2021'
 * formatDateByLocale('2021-02-03T14:42:14.177Z', 'de-DE'); // '03.02.2021'
 * formatDateByLocale('2021-02-03T14:42:14.177Z', 'nl-BE'); // '03-02-2021'
 * formatDateByLocale('2021-02-03T14:42:14.177Z', 'nl-NL'); // '03-02-2021'
 */
export function formatDateByLocale(
  dateString: string | undefined,
  locale: LocaleIso = 'de-DE',
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Default options for date formatting
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...options,
    };

    const normalizedLocale = locale === 'nl-BE' ? 'nl-NL' : locale;

    return new Intl.DateTimeFormat(normalizedLocale, defaultOptions).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);

    return '';
  }
}

/**
 * Formats a date string as a short date (e.g., '03.02.2021' for German, '03/02/2021' for British English).
 *
 * @param dateString - The ISO date string to format.
 * @param locale - The locale to use for formatting. Defaults to 'de-DE'.
 * @returns The formatted short date string, or an empty string if input is invalid.
 *
 * @example
 * formatShortDate('2021-02-03T14:42:14.177Z', 'de-DE'); // '03.02.2021'
 * formatShortDate('2021-02-03T14:42:14.177Z', 'en-GB'); // '03/02/2021'
 */
export function formatShortDate(dateString: string | undefined, locale: LocaleIso = 'de-DE'): string {
  return formatDateByLocale(dateString, locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Formats a date string as a long date with weekday and month name (e.g., 'Mittwoch, 3. Februar 2021').
 *
 * @param dateString - The ISO date string to format.
 * @param locale - The locale to use for formatting. Defaults to 'de-DE'.
 * @returns The formatted long date string, or an empty string if input is invalid.
 *
 * @example
 * formatLongDate('2021-02-03T14:42:14.177Z', 'de-DE'); // 'Mittwoch, 3. Februar 2021'
 * formatLongDate('2021-02-03T14:42:14.177Z', 'en-GB'); // 'Wednesday, 3 February 2021'
 */
export function formatLongDate(dateString: string | undefined, locale: LocaleIso = 'de-DE'): string {
  return formatDateByLocale(dateString, locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Formats a date string as a date and time (e.g., '03.02.2021, 15:42' for German).
 *
 * @param dateString - The ISO date string to format.
 * @param locale - The locale to use for formatting. Defaults to 'de-DE'.
 * @returns The formatted date and time string, or an empty string if input is invalid.
 *
 * @example
 * formatDateTime('2021-02-03T14:42:14.177Z', 'de-DE'); // '03.02.2021, 15:42'
 * formatDateTime('2021-02-03T14:42:14.177Z', 'en-GB'); // '03/02/2021, 14:42'
 */
export function formatDateTime(dateString: string | undefined, locale: LocaleIso = 'de-DE'): string {
  return formatDateByLocale(dateString, locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
